import React from 'react';
import {
  Clock, ShoppingCart, DollarSign, Users, Package, CheckCircle, TrendingUp,
  CreditCard, AlertTriangle, FileText,
} from 'lucide-react';
import {
  ResponsiveContainer, LineChart, CartesianGrid, XAxis, YAxis, Tooltip, Legend,
  Line, PieChart, Pie, Cell, BarChart, Bar,
} from 'recharts';

const DashboardOverviewTab = ({ dashboardOverview, adminData, orders, setActiveTab }) => {
  const {
    dashboardStats, dashboardAnalytics, analyticsTimeRange, setAnalyticsTimeRange,
    loading, refundStats, handleViewLowStockDetails,
  } = dashboardOverview;

  const orderStatusData = dashboardStats ? [
    { name: 'Pending', value: dashboardStats.pendingOrders || 0, color: '#f59e0b' },
    { name: 'Confirmed', value: dashboardStats.confirmedOrders || 0, color: '#3b82f6' },
    { name: 'Shipped', value: dashboardStats.shippedOrders || 0, color: '#8b5cf6' },
    { name: 'Delivered', value: dashboardStats.deliveredOrders || 0, color: '#22c55e' },
    { name: 'Cancelled', value: dashboardStats.cancelledOrders || 0, color: '#dc2626' },
  ].filter((item) => item.value > 0) : [];

  const paymentMethodData = dashboardStats ? [
    { name: 'M-Pesa', value: dashboardStats.mpesaPayments || 0, color: '#22c55e' },
    { name: 'Credit Card', value: dashboardStats.cardPayments || 0, color: '#3b82f6' },
    { name: 'Cash on Delivery', value: dashboardStats.codPayments || 0, color: '#f59e0b' },
  ].filter((item) => item.value > 0) : [];

  return (
    <div className="p-6">
      <div className="flex justify-between items-center mb-6">
        <div>
          <h2 className="text-2xl font-bold text-gray-800">Admin Dashboard</h2>
          <p className="text-sm text-gray-600 mt-1">Welcome back, {adminData?.fullName || 'Admin'}</p>
        </div>
        <div className="flex items-center space-x-4">
          <div className="flex items-center space-x-2">
            <span className="text-sm text-gray-600 font-medium">Time Range:</span>
            <select
              value={analyticsTimeRange}
              onChange={(e) => setAnalyticsTimeRange(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#d44100] text-sm"
            >
              <option value="7">Last 7 Days</option>
              <option value="30">Last 30 Days</option>
              <option value="90">Last 90 Days</option>
              <option value="365">Last Year</option>
              <option value="all">All Time</option>
            </select>
          </div>
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Clock className="h-4 w-4" />
            <span>Last updated: {new Date().toLocaleTimeString()}</span>
          </div>
        </div>
      </div>

      {loading ? (
        <div className="flex justify-center items-center h-64">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#d44100]"></div>
        </div>
      ) : (
        <>
          {/* Key Metrics Cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-gradient-to-br from-blue-500 to-blue-600 rounded-lg shadow-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-blue-100">Total Orders</p>
                  <p className="text-3xl font-bold mt-2">{dashboardStats?.totalOrders || 0}</p>
                  <p className="text-xs text-blue-100 mt-1">
                    {dashboardStats?.pendingOrders || 0} pending
                  </p>
                </div>
                <ShoppingCart className="h-12 w-12 text-blue-200" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-green-500 to-green-600 rounded-lg shadow-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-green-100">Total Revenue</p>
                  <p className="text-3xl font-bold mt-2">KSh {dashboardStats?.totalRevenue?.toLocaleString() || 0}</p>
                  <p className="text-xs text-green-100 mt-1">
                    {analyticsTimeRange === 'all'
                      ? 'All time revenue'
                      : analyticsTimeRange === '7'
                      ? 'Last 7 days'
                      : analyticsTimeRange === '30'
                      ? 'Last 30 days'
                      : analyticsTimeRange === '90'
                      ? 'Last 90 days'
                      : 'Last year'}
                  </p>
                </div>
                <DollarSign className="h-12 w-12 text-green-200" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-purple-500 to-purple-600 rounded-lg shadow-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-purple-100">Total Customers</p>
                  <p className="text-3xl font-bold mt-2">{dashboardStats?.totalCustomers || 0}</p>
                  <p className="text-xs text-purple-100 mt-1">
                    {analyticsTimeRange === 'all'
                      ? `${dashboardStats?.newCustomersThisMonth || 0} new this month`
                      : analyticsTimeRange === '7'
                      ? 'Last 7 days'
                      : analyticsTimeRange === '30'
                      ? 'Last 30 days'
                      : analyticsTimeRange === '90'
                      ? 'Last 90 days'
                      : 'Last year'}
                  </p>
                </div>
                <Users className="h-12 w-12 text-purple-200" />
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg shadow-lg p-6 text-white">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-orange-100">Total Products</p>
                  <p className="text-3xl font-bold mt-2">{dashboardStats?.totalProducts || 0}</p>
                  <p className="text-xs text-orange-100 mt-1">
                    {dashboardStats?.lowStockProducts || 0} low stock
                  </p>
                </div>
                <Package className="h-12 w-12 text-orange-200" />
              </div>
            </div>
          </div>

          {/* Secondary Metrics */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
            <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-yellow-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Pending Orders</p>
                  <p className="text-2xl font-bold text-gray-900">{dashboardStats?.pendingOrders || 0}</p>
                </div>
                <Clock className="h-8 w-8 text-yellow-500" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-blue-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Confirmed Orders</p>
                  <p className="text-2xl font-bold text-gray-900">{dashboardStats?.confirmedOrders || 0}</p>
                </div>
                <CheckCircle className="h-8 w-8 text-blue-500" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-red-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Total Refunds</p>
                  <p className="text-2xl font-bold text-gray-900">{refundStats?.total || 0}</p>
                  <p className="text-xs text-gray-500">KSh {refundStats?.totalAmount?.toLocaleString() || 0}</p>
                </div>
                <DollarSign className="h-8 w-8 text-red-500" />
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6 border-l-4 border-green-500">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm font-medium text-gray-600">Avg Order Value</p>
                  <p className="text-2xl font-bold text-gray-900">
                    KSh {dashboardStats?.averageOrderValue?.toLocaleString() || 0}
                  </p>
                </div>
                <TrendingUp className="h-8 w-8 text-green-500" />
              </div>
            </div>
          </div>

          {/* Main Content Grid */}
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 mb-8">
            {/* Order Status Breakdown */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Order Status Breakdown</h3>
              <div className="space-y-3">
                {[
                  { status: 'PENDING', count: dashboardStats?.pendingOrders || 0, color: 'yellow' },
                  { status: 'CONFIRMED', count: dashboardStats?.confirmedOrders || 0, color: 'blue' },
                  { status: 'SHIPPED', count: dashboardStats?.shippedOrders || 0, color: 'purple' },
                  { status: 'DELIVERED', count: dashboardStats?.deliveredOrders || 0, color: 'green' },
                  { status: 'CANCELLED', count: dashboardStats?.cancelledOrders || 0, color: 'red' },
                ].map((item) => (
                  <div key={item.status} className="flex items-center justify-between">
                    <span className="text-sm text-gray-600">{item.status}</span>
                    <div className="flex items-center space-x-2">
                      <div className="w-24 bg-gray-200 rounded-full h-2">
                        <div
                          className={`bg-${item.color}-500 h-2 rounded-full`}
                          style={{
                            width: `${dashboardStats?.totalOrders ? (item.count / dashboardStats.totalOrders) * 100 : 0}%`,
                          }}
                        ></div>
                      </div>
                      <span className="text-sm font-semibold text-gray-900 w-8 text-right">{item.count}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Payment Method Breakdown */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h3 className="text-lg font-semibold text-gray-800 mb-4">Payment Methods</h3>
              <div className="space-y-3">
                {[
                  { method: 'M-Pesa', count: dashboardStats?.mpesaPayments || 0, color: 'green' },
                  { method: 'Credit Card', count: dashboardStats?.cardPayments || 0, color: 'blue' },
                  { method: 'Cash on Delivery', count: dashboardStats?.codPayments || 0, color: 'orange' },
                ].map((item) => (
                  <div key={item.method} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
                    <div className="flex items-center">
                      <CreditCard className={`h-5 w-5 text-${item.color}-500 mr-2`} />
                      <span className="text-sm font-medium text-gray-700">{item.method}</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900">{item.count}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Low Stock Alert */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Low Stock Alert</h3>
                <AlertTriangle className="h-6 w-6 text-red-500" />
              </div>
              <div className="flex items-center justify-between p-4 bg-red-50 rounded-lg">
                <div>
                  <p className="text-2xl font-bold text-red-600">{dashboardStats?.lowStockProducts || 0}</p>
                  <p className="text-sm text-gray-600">products need restocking</p>
                </div>
                <button
                  onClick={handleViewLowStockDetails}
                  className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm font-medium"
                >
                  View Details
                </button>
              </div>
            </div>
          </div>

          {/* Recent Activity & Orders */}
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Recent Orders</h3>
                <button
                  onClick={() => setActiveTab('orders')}
                  className="text-sm text-[#d44100] hover:text-[#b83700] font-medium"
                >
                  View All
                </button>
              </div>
              <div className="space-y-3">
                {dashboardStats?.recentOrders?.slice(0, 5).map((order) => (
                  <div
                    key={order.id}
                    className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors cursor-pointer"
                    onClick={() => orders.handleViewOrder(order.id)}
                  >
                    <div className="flex-1">
                      <p className="font-medium text-gray-800">#{order.orderNumber}</p>
                      <p className="text-sm text-gray-600">{order.customer?.fullName || 'Unknown Customer'}</p>
                    </div>
                    <div className="text-right">
                      <p className="font-medium text-gray-800">KSh {order.total?.toLocaleString() || 0}</p>
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        order.status === 'PENDING' ? 'bg-yellow-100 text-yellow-800' :
                        order.status === 'CONFIRMED' ? 'bg-blue-100 text-blue-800' :
                        order.status === 'SHIPPED' ? 'bg-purple-100 text-purple-800' :
                        order.status === 'DELIVERED' ? 'bg-green-100 text-green-800' :
                        'bg-red-100 text-red-800'
                      }`}>
                        {order.status}
                      </span>
                    </div>
                  </div>
                ))}
                {(!dashboardStats?.recentOrders || dashboardStats.recentOrders.length === 0) && (
                  <p className="text-center text-gray-500 py-4">No recent orders</p>
                )}
              </div>
            </div>

            <div className="bg-white rounded-lg shadow-md p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold text-gray-800">Quick Actions</h3>
              </div>
              <div className="grid grid-cols-2 gap-3">
                <button
                  onClick={() => setActiveTab('inventory')}
                  className="p-4 bg-blue-50 rounded-lg hover:bg-blue-100 transition-colors text-left"
                >
                  <Package className="h-6 w-6 text-blue-600 mb-2" />
                  <p className="font-medium text-gray-800">Manage Inventory</p>
                  <p className="text-xs text-gray-600">Add or edit products</p>
                </button>
                <button
                  onClick={() => setActiveTab('orders')}
                  className="p-4 bg-green-50 rounded-lg hover:bg-green-100 transition-colors text-left"
                >
                  <ShoppingCart className="h-6 w-6 text-green-600 mb-2" />
                  <p className="font-medium text-gray-800">View Orders</p>
                  <p className="text-xs text-gray-600">Process orders</p>
                </button>
                <button
                  onClick={() => setActiveTab('payments')}
                  className="p-4 bg-purple-50 rounded-lg hover:bg-purple-100 transition-colors text-left"
                >
                  <CreditCard className="h-6 w-6 text-purple-600 mb-2" />
                  <p className="font-medium text-gray-800">Payments</p>
                  <p className="text-xs text-gray-600">View transactions</p>
                </button>
                <button
                  onClick={() => setActiveTab('reports')}
                  className="p-4 bg-orange-50 rounded-lg hover:bg-orange-100 transition-colors text-left"
                >
                  <FileText className="h-6 w-6 text-orange-600 mb-2" />
                  <p className="font-medium text-gray-800">Reports</p>
                  <p className="text-xs text-gray-600">View analytics</p>
                </button>
              </div>
            </div>
          </div>

          {/* Advanced Analytics Charts Section */}
          {dashboardAnalytics && (
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 mt-8">
              {/* Revenue Trends */}
              {dashboardAnalytics.revenueTrends && dashboardAnalytics.revenueTrends.length > 0 && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Revenue Trends</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={dashboardAnalytics.revenueTrends}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip
                        formatter={(value) => [`KSh ${value.toLocaleString()}`, 'Revenue']}
                        labelStyle={{ color: '#333' }}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="revenue"
                        stroke="#d44100"
                        strokeWidth={2}
                        name="Revenue"
                        dot={{ fill: '#d44100', r: 4 }}
                      />
                      <Line
                        type="monotone"
                        dataKey="orders"
                        stroke="#0f4c5c"
                        strokeWidth={2}
                        name="Orders"
                        dot={{ fill: '#0f4c5c', r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Order Status Pie Chart */}
              {orderStatusData.length > 0 && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Order Status Distribution</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={orderStatusData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {orderStatusData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Top Products */}
              {dashboardAnalytics.topProducts && dashboardAnalytics.topProducts.length > 0 && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Top Products</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <BarChart data={dashboardAnalytics.topProducts.slice(0, 10)}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="name"
                        angle={-45}
                        textAnchor="end"
                        height={100}
                        tick={{ fontSize: 10 }}
                      />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip
                        formatter={(value) => [value.toLocaleString(), 'Quantity']}
                        labelStyle={{ color: '#333' }}
                      />
                      <Legend />
                      <Bar dataKey="quantity" fill="#d44100" name="Quantity Sold" />
                      <Bar dataKey="revenue" fill="#0f4c5c" name="Revenue (KSh)" />
                    </BarChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Payment Methods Pie Chart */}
              {paymentMethodData.length > 0 && (
                <div className="bg-white rounded-lg shadow-md p-6">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Payment Methods Distribution</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <PieChart>
                      <Pie
                        data={paymentMethodData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={({ name, percent }) => `${name}: ${(percent * 100).toFixed(0)}%`}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {paymentMethodData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Tooltip />
                    </PieChart>
                  </ResponsiveContainer>
                </div>
              )}

              {/* Customer Growth */}
              {dashboardAnalytics.customerGrowth && dashboardAnalytics.customerGrowth.length > 0 && (
                <div className="bg-white rounded-lg shadow-md p-6 lg:col-span-2">
                  <h3 className="text-lg font-semibold text-gray-800 mb-4">Customer Growth</h3>
                  <ResponsiveContainer width="100%" height={300}>
                    <LineChart data={dashboardAnalytics.customerGrowth}>
                      <CartesianGrid strokeDasharray="3 3" />
                      <XAxis
                        dataKey="date"
                        tick={{ fontSize: 12 }}
                        angle={-45}
                        textAnchor="end"
                        height={80}
                      />
                      <YAxis tick={{ fontSize: 12 }} />
                      <Tooltip
                        formatter={(value) => [value, 'New Customers']}
                        labelStyle={{ color: '#333' }}
                      />
                      <Legend />
                      <Line
                        type="monotone"
                        dataKey="customers"
                        stroke="#22c55e"
                        strokeWidth={2}
                        name="New Customers"
                        dot={{ fill: '#22c55e', r: 4 }}
                      />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              )}
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default DashboardOverviewTab;
